import { useCallback } from 'react';

import { is } from 'utils/helpers';
import { ENTITIES } from 'utils/string';

import { useNumberFormatter } from './useNumberFormatter';

/**
 * Format a raw byte value to a human friendly string.
 *
 * Core logic from https://github.com/sindresorhus/pretty-bytes.
 *
 * @example
 *
 * formatBytes(123456)
 * // => '123 kB'
 */
export function useByteFormatter() {
	const formatDecimal = useNumberFormatter('decimal');
	const formatInteger = useNumberFormatter('integer');

	return useCallback(
		(bytes: number | undefined | null): string => {
			if (!is.number(bytes)) {
				return '';
			}
			const units = ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

			const nbsp = ENTITIES.noBreakSpace;
			const isNegative = bytes < 0;
			const prefix = isNegative ? ENTITIES.minus : '';

			let num = bytes;
			if (isNegative) {
				num = -num;
			}

			if (num < 1) {
				return `${prefix}${formatDecimal(num)}${nbsp}${units[0]}`;
			}

			const exponent = Math.min(
				Math.floor(Math.log10(num) / 3),
				units.length - 1,
			);
			num /= 1000 ** exponent;
			const unit = units[exponent];

			return `${prefix}${formatInteger(Number(num.toPrecision(3)))}${nbsp}${unit}`;
		},
		[formatDecimal, formatInteger],
	);
}
